import { DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY } from "@org-avp/avp-avengers-ui-shared-utils";
import { Button, ButtonProps, Tooltip, TooltipProps } from "antd";
import { forwardRef, UIEvent, useCallback, useMemo } from "react";

export type ToggleButtonStatePropsType = Omit<ButtonProps, "onClick">;

export interface ToggleButtonStateProps {
  commonProps?: ToggleButtonStatePropsType;
  trueProps: ToggleButtonStatePropsType;
  falseProps: ToggleButtonStatePropsType;
}

export type ToggleButtonProps = {
  stateProps: ToggleButtonStateProps;
  value: boolean;
  toggle: () => void;
  tooltipPlacement?: TooltipProps["placement"];
  mouseEnterDelay?: TooltipProps["mouseEnterDelay"];
};

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  function ToggleButton({ stateProps, value, toggle, ...props }, ref) {
    const [trueProps, falseProps] = useMemo(
      () => [
        { ...stateProps.commonProps, ...stateProps.trueProps },
        { ...stateProps.commonProps, ...stateProps.falseProps },
      ],
      [stateProps.commonProps, stateProps.falseProps, stateProps.trueProps],
    );

    const onClick = useCallback(
      (e: UIEvent) => {
        e.stopPropagation();
        toggle();
      },
      [toggle],
    );

    const [buttonProps, title]: [ButtonProps, string | undefined] = useMemo(
      () => ([
        {
          onClick,
          ...(value ? trueProps : falseProps),
          title: undefined,
        },
        value ? trueProps.title : falseProps.title,
      ]),
      [onClick, value, trueProps, falseProps],
    );

    const tooltipProps = useMemo(
      () => {
        const delay = props.mouseEnterDelay;
        return {
          placement: props.tooltipPlacement,
          mouseEnterDelay: delay !== undefined ? delay : DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY,
        };
      },
      [props.mouseEnterDelay, props.tooltipPlacement],
    );

    const button = useMemo(() => <Button ref={ref} {...buttonProps}/>, [buttonProps, ref]);
    return title ? <Tooltip title={title} {...tooltipProps}>{button}</Tooltip> : button;
  },
);
